import React, { useContext, useEffect, useState } from 'react'
import API from '../../../services/API'
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { GlobalContext } from '../../../App'
import { regionLabels } from '../../../helpers/region'
import { Add, Close, LocalHospitalOutlined } from '@mui/icons-material'
import OrganisationSelector from '../NewBooking/OrganisationSelector'

const HospitalNumbers = ({
  hospitalNumbers,
  setHospitalNumbers,
  fixedOrganisation = null,
}) => {
  const { tenantInfo } = useContext(GlobalContext)
  const [newMRN, setNewMRN] = useState('')
  const [newOrg, setNewOrg] = useState(null)
  const [orgOptions, setOrgOptions] = useState([])

  useEffect(() => {
    if (fixedOrganisation) return
    API.get('/pts/organisationoptions').then((res) => {
      if (res.status === 200) setOrgOptions(res.data)
    })
  }, [fixedOrganisation])

  const label = regionLabels(tenantInfo?.region).hospitalNumberNoun

  const addNumber = () => {
    const organisation = fixedOrganisation || newOrg
    if (!newMRN || !organisation) return

    setHospitalNumbers((prev) => [
      ...prev,
      {
        organisation,
        mrn: newMRN,
      },
    ])

    setNewMRN('')
    setNewOrg(null)
  }

  const removeNumber = (indexToRemove) => {
    setHospitalNumbers((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    )
  }

  return (
    <Grid item xs={12}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 3,
          backgroundColor: 'background.default',
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={1}>
            <LocalHospitalOutlined color="contrast" sx={{ mt: 1 }} />

            <Box flex={1}>
              <Typography fontWeight={700} color="contrast.main">
                {label} Numbers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add patient identifiers for hospitals or receiving
                organisations.
              </Typography>
            </Box>
          </Stack>

          {hospitalNumbers?.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {hospitalNumbers.map((x, index) => (
                <Chip
                  key={`${x?.mrn}-${index}`}
                  label={`${x?.mrn} · ${x?.organisation?.orgName ?? 'Unknown organisation'}`}
                  onDelete={() => removeNumber(index)}
                  deleteIcon={<Close />}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    py: 2.4,
                    fontWeight: 600,
                    backgroundColor: 'background.paper',
                  }}
                />
              ))}
            </Stack>
          )}
          <Box>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={regionLabels(tenantInfo?.region).hospitalNumberLabel}
                  value={newMRN}
                  onChange={(e) => setNewMRN(e.target.value)}
                />
              </Grid>

              {fixedOrganisation ? (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Hospital"
                    value={fixedOrganisation?.orgName || 'Your hospital'}
                    disabled
                  />
                </Grid>
              ) : (
                <Grid item xs={12} md={6}>
                  <OrganisationSelector
                    value={newOrg}
                    onChange={(o) => setNewOrg(o)}
                    options={orgOptions}
                    label="Hospital"
                    showAddButton={false}
                    hideHelperText
                  />
                </Grid>
              )}

              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<Add />}
                  onClick={addNumber}
                  disabled={!newMRN || !(fixedOrganisation || newOrg)}
                >
                  Add
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </Paper>
    </Grid>
  )
}

export default HospitalNumbers
